/* Atelier — product page.
   <atelier-product>: set rows pick the variant (hidden form inputs, Add to bag price, set photo, URL ?variant=,
   cost per day, Shop Pay Installments), thumbnails switch the main image (the product film plays first, muted),
   the delivery estimate is computed here,
   and links with [data-atelier-select-variant] (e.g. the gift section) select a set instead of reloading.
   Other sections stay in sync through the document event "atelier:variant-change" { productId, variantId, source }. */
if (!customElements.get('atelier-product')) {
  customElements.define(
    'atelier-product',
    class AtelierProduct extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready) return;
        this.dataset.ready = 'true';
        this.productId = String(this.dataset.productId || '');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.onClick = this.onClick.bind(this);
        this.onExternalChange = this.onExternalChange.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onResize = this.onResize.bind(this);

        this.addEventListener('click', this.onClick);
        document.addEventListener('atelier:variant-change', this.onExternalChange);
        document.addEventListener('click', this.onDocumentClick);
        window.addEventListener('resize', this.onResize);

        // Theme editor: show the slide or select the set whose block is selected.
        this.addEventListener('shopify:block:select', (event) => {
          const target = event.target;
          if (target.matches && target.matches('[data-variant-id]')) this.select(target.dataset.variantId);
          if (target.matches && target.matches('[data-slide]')) this.showSlide(target.dataset.slide);
        });

        this.renderDelivery();
        this.startFilm();
        this.scheduleFit();
        if (window.customElements && customElements.whenDefined) {
          customElements.whenDefined('shopify-payment-terms').then(() => this.scheduleFit());
        }
      }

      disconnectedCallback() {
        clearTimeout(this.deliveryTimer);
        document.removeEventListener('atelier:variant-change', this.onExternalChange);
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('resize', this.onResize);
      }

      get rows() {
        return Array.from(this.querySelectorAll('.at-pdp__set[data-variant-id]'));
      }

      hasVariant(id) {
        return this.rows.some((row) => row.dataset.variantId === String(id));
      }

      onClick(event) {
        const row = event.target.closest('.at-pdp__set[data-variant-id]');
        if (row && this.contains(row)) {
          this.select(row.dataset.variantId);
          return;
        }
        const thumb = event.target.closest('[data-slide-to]');
        if (thumb && this.contains(thumb)) this.showSlide(thumb.dataset.slideTo);
      }

      onDocumentClick(event) {
        const link = event.target.closest('[data-atelier-select-variant]');
        if (!link || this.contains(link)) return;
        const id = String(link.dataset.atelierSelectVariant);
        if (!this.hasVariant(id)) return;
        event.preventDefault();
        this.select(id);
        this.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'start' });
      }

      onExternalChange(event) {
        const detail = event.detail || {};
        if (detail.source === this || String(detail.productId) !== this.productId) return;
        this.select(detail.variantId, { silent: true });
      }

      onResize() {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.fitTerms(), 150);
      }

      select(variantId, options = {}) {
        const id = String(variantId);
        const row = this.rows.find((item) => item.dataset.variantId === id);
        if (!row) return false;

        this.rows.forEach((item) => item.setAttribute('aria-pressed', item === row ? 'true' : 'false'));
        this.querySelectorAll('[data-set-image]').forEach((el) => {
          el.hidden = el.dataset.setImage !== id;
        });
        this.querySelectorAll('[data-set-name]').forEach((el) => {
          el.textContent = row.dataset.name || '';
        });
        this.querySelectorAll('[data-at-per-day]').forEach((el) => {
          el.textContent = row.dataset.perDay || '';
        });

        // Both product forms (Add to bag + express checkout, and Shop Pay Installments) follow the hidden id input.
        this.querySelectorAll('form input[name="id"]').forEach((input) => {
          if (input.value === id) return;
          input.value = id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        const available = row.dataset.available === 'true';
        const button = this.querySelector('[data-at-add]');
        if (button) {
          button.disabled = !available;
          if (available) button.removeAttribute('aria-disabled');
        }
        const label = this.querySelector('[data-at-add-label]');
        if (label) label.textContent = available ? this.dataset.addLabel : this.dataset.soldOutLabel;
        const price = this.querySelector('[data-at-price]');
        if (price) price.textContent = row.dataset.price || '';

        this.showSlide(0);
        this.updateUrl(id);
        this.scheduleFit();

        if (!options.silent) {
          document.dispatchEvent(
            new CustomEvent('atelier:variant-change', {
              detail: { productId: this.productId, variantId: id, source: this },
            })
          );
        }
        return true;
      }

      updateUrl(id) {
        if (this.dataset.updateUrl === 'false' || (window.Shopify && window.Shopify.designMode)) return;
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', id);
          window.history.replaceState(window.history.state, '', url.toString());
        } catch (e) {
          /* ignore */
        }
      }

      showSlide(index) {
        const key = String(index);
        if (!this.querySelector(`.at-pdp__slide[data-slide="${key}"]`)) return;
        this.querySelectorAll('.at-pdp__slide[data-slide]').forEach((slide) => {
          const on = slide.dataset.slide === key;
          slide.hidden = !on;
          const video = slide.querySelector('video');
          if (!video) return;
          if (on) {
            video.muted = true;
            video.setAttribute('playsinline', '');
            if (this.reducedMotion) {
              video.controls = true;
            } else {
              const playing = video.play();
              if (playing && playing.catch) playing.catch(() => {});
            }
          } else {
            video.pause();
          }
        });
        this.querySelectorAll('[data-slide-to]').forEach((thumb) => {
          thumb.setAttribute('aria-pressed', thumb.dataset.slideTo === key ? 'true' : 'false');
        });
      }

      /* The film leads the gallery: it autoplays muted, inline and on a loop (the markup carries autoplay, muted and
         playsinline for iOS); this nudges browsers that held it back, and keeps it still for reduced motion. */
      startFilm() {
        const slide = this.querySelector('.at-pdp__slide--video:not([hidden])');
        const video = slide && slide.querySelector('video');
        if (!video) return;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        if (this.reducedMotion) {
          video.removeAttribute('autoplay');
          video.pause();
          video.controls = true;
          return;
        }
        const play = () => {
          if (slide.hidden || !video.paused) return;
          const playing = video.play();
          if (playing && playing.catch) playing.catch(() => {});
        };
        if (video.readyState >= 2) play();
        else video.addEventListener('loadeddata', play, { once: true });
        play();

        // Recovery: if the film errors, or stalls for more than 4 s, reload it and resume (twice at most), so a
        // browser that gives up on the stream doesn't leave the slide empty.
        let retries = 0;
        let stallTimer = null;
        const recover = () => {
          clearTimeout(stallTimer);
          if (retries >= 2 || slide.hidden) return;
          retries++;
          const t = video.currentTime || 0;
          video.load();
          video.addEventListener('loadeddata', () => {
            try { video.currentTime = t; } catch (e) {}
            play();
          }, { once: true });
        };
        video.addEventListener('error', recover);
        video.querySelectorAll('source').forEach((src) => src.addEventListener('error', recover));
        const watch = () => {
          clearTimeout(stallTimer);
          stallTimer = setTimeout(() => { if (!video.paused && video.readyState < 3) recover(); }, 4000);
        };
        video.addEventListener('waiting', watch);
        video.addEventListener('stalled', watch);
        video.addEventListener('playing', () => clearTimeout(stallTimer));
      }

      /* Delivery estimate in Eastern time (America/New_York, DST-aware through Intl), whatever the visitor's clock, in
         business days (Mon-Fri, default, per the shipping policy) or calendar days: an order before the cutoff counts from
         that day (the next business day on weekends), a later one from the next day; it ships within [ship-days] days and
         arrives [arrive-min]–[arrive-max] calendar days after that (carriers deliver at weekends; business days if the
         section's "Count arrival in business days" is on). The countdown (optional) runs to the cutoff of the order day. */
      easternNow() {
        const parts = {};
        try {
          new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/New_York',
            hourCycle: 'h23',
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
          })
            .formatToParts(new Date())
            .forEach((p) => {
              parts[p.type] = p.value;
            });
        } catch (e) {
          const d = new Date();
          return { date: Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()), seconds: d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds() };
        }
        const hour = parseInt(parts.hour, 10) % 24;
        return {
          // the Eastern calendar day as a UTC midnight (day arithmetic without time zones)
          date: Date.UTC(parseInt(parts.year, 10), parseInt(parts.month, 10) - 1, parseInt(parts.day, 10)),
          seconds: hour * 3600 + parseInt(parts.minute, 10) * 60 + parseInt(parts.second, 10),
        };
      }

      renderDelivery() {
        const box = this.querySelector('[data-at-delivery]');
        if (!box) return;
        const DAY = 86400000;
        const int = (v, d) => (Number.isNaN(parseInt(v, 10)) ? d : parseInt(v, 10));
        const shipDays = int(box.dataset.shipDays, 0);
        const arriveMin = int(box.dataset.arriveMin, 2);
        const arriveMax = Math.max(int(box.dataset.arriveMax, 3), arriveMin);
        const cutoff = int(box.dataset.cutoffHour, 14) * 3600;
        const prefix = box.dataset.shipsPrefix || '';
        const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const DAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const now = this.easternNow();
        // business days (Mon-Fri, the shipping policy's terms) or calendar days, per the section setting
        const business = box.dataset.businessDays === 'true';
        // arrival counts calendar days (carriers deliver at weekends) unless "Count arrival in business days" is on
        const arriveBusiness = box.dataset.arriveBusiness === 'true';
        const isBusiness = (t) => {
          const w = new Date(t).getUTCDay();
          return w !== 0 && w !== 6;
        };
        const addDays = (t, n, biz = business) => {
          if (!biz) return t + n * DAY;
          let d = t;
          let k = 0;
          while (k < n) {
            d += DAY;
            if (isBusiness(d)) k++;
          }
          return d;
        };
        let orderDay = now.date;
        if (now.seconds >= cutoff) orderDay += DAY;
        if (business) while (!isBusiness(orderDay)) orderDay += DAY;
        const ship = addDays(orderDay, shipDays);
        const a = addDays(ship, arriveMin, arriveBusiness);
        const b = addDays(ship, arriveMax, arriveBusiness);
        const D = (t) => new Date(t);

        const ships = this.querySelector('[data-at-ships]');
        if (ships) {
          const inDays = Math.round((ship - now.date) / DAY);
          ships.textContent =
            inDays === 0
              ? 'Today'
              : inDays === 1
                ? 'Tomorrow'
                : `${prefix ? `${prefix} ` : ''}${DAY_SHORT[D(ship).getUTCDay()]}, ${MON[D(ship).getUTCMonth()]} ${D(ship).getUTCDate()}`;
        }
        const arrives = this.querySelector('[data-at-arrives]');
        if (arrives) {
          const sameMonth = D(a).getUTCMonth() === D(b).getUTCMonth();
          arrives.textContent =
            a === b
              ? `${MON[D(a).getUTCMonth()]} ${D(a).getUTCDate()}`
              : `${MON[D(a).getUTCMonth()]} ${D(a).getUTCDate()} – ${sameMonth ? '' : `${MON[D(b).getUTCMonth()]} `}${D(b).getUTCDate()}`;
        }

        const countdown = this.querySelector('[data-at-countdown]');
        const template = box.dataset.countdownText || '';
        if (countdown && template) {
          const left = Math.max(0, Math.round((orderDay - now.date) / DAY) * 86400 + cutoff - now.seconds);
          const mins = Math.max(1, Math.ceil(left / 60));
          const h = Math.floor(mins / 60);
          const m = mins % 60;
          const time = h >= 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : h > 0 ? (m ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
          const days = Math.round((ship - now.date) / DAY);
          const day = days === 0 ? 'today' : days === 1 ? 'tomorrow' : DAY_LONG[D(ship).getUTCDay()];
          const esc = (v) => String(v).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
          countdown.innerHTML = esc(template)
            .replace('[time]', `<strong>${esc(time)}</strong>`)
            .replace('[day]', `<strong>${esc(day)}</strong>`);
          countdown.hidden = false;
        }

        // tick: the countdown each 30 s, and the dates as the cutoff or midnight passes
        clearTimeout(this.deliveryTimer);
        this.deliveryTimer = setTimeout(() => this.renderDelivery(), 30000);
      }

      /* Shop Pay Installments stays on one line: shrink its type until it fits the column. */
      scheduleFit() {
        [0, 400, 1200, 2500].forEach((delay) => setTimeout(() => this.fitTerms(), delay));
      }

      /* The installments line is Shopify's own element: give it the page's type (Figtree, the amount in ink at 600) and a
         question-mark button at text height, through a stylesheet adopted by its open shadow root. */
      styleTerms() {
        const terms = this.querySelector('.at-pdp__terms shopify-payment-terms');
        const root = terms && terms.shadowRoot;
        if (!root || !('adoptedStyleSheets' in root)) return;
        try {
          if (!this.termsSheet) {
            this.termsSheet = new CSSStyleSheet();
            this.termsSheet.replaceSync(
              '#shopify-installments{font-family:inherit;}' +
                '#shopify-installments .font-bold{font-weight:600;color:var(--at-ink,#1E2823);}' +
                '#shopify-installments-cta{padding:0 0 0 2px;vertical-align:middle;line-height:0;}' +
                '#shopify-installments-cta svg{width:1.2em;height:1.2em;}'
            );
          }
          if (!root.adoptedStyleSheets.includes(this.termsSheet)) {
            root.adoptedStyleSheets = [...root.adoptedStyleSheets, this.termsSheet];
          }
        } catch (e) {
          /* constructable stylesheets unsupported: Shopify's own styling stays */
        }
      }

      fitTerms() {
        this.styleTerms();
        const box = this.querySelector('.at-pdp__terms');
        if (!box || !box.clientWidth) return;
        box.style.fontSize = '';
        let size = parseFloat(window.getComputedStyle(box).fontSize) || 12;
        let guard = 0;
        while (box.scrollWidth > box.clientWidth + 1 && size > 9 && guard < 12) {
          size -= 0.5;
          guard++;
          box.style.fontSize = `${size}px`;
        }
      }
    }
  );
}
